/* eslint-disable max-lines-per-function */
import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import { xray } from 'lib/xray';
import React, { type ReactElement, memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionTermsLink, NEW_ACTION_TERMS_LINK } from 'reactApp/appHelpers/actionSettings';
import { IS_ANDROID_MOBILE, IS_IOS_MOBILE, IS_MOBILE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { isDarkTariffsPages, isRebranding, isRebrandingQuotaLanding } from 'reactApp/appHelpers/featuresHelpers';
import { promoQuotaAllTariffsLink } from 'reactApp/appHelpers/featuresHelpers/features/allTariffsLink';
import { blackFriday2025 } from 'reactApp/appHelpers/featuresHelpers/features/blackFriday2025';
import { mailSpaceBenifits } from 'reactApp/appHelpers/featuresHelpers/features/mailSpaceBenefits';
import { multiTariffMobile } from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { useDarkTheme } from 'reactApp/hooks/useDarkTheme';
import { useMarchPromo2026MailSpace } from 'reactApp/hooks/useMarchPromo2026';
import { clearRebrandingQuota, setRebrandingQuota } from 'reactApp/hooks/useRebranding';
import { useSummerPromo2026 } from 'reactApp/hooks/useSummerPromo2026';
import { checkMobileWebviewPayAvailable } from 'reactApp/modules/billing/billing.helpers';
import { getPastSubscriptionsIfNotLoaded } from 'reactApp/modules/pastSubscriptions/pastSubscriptions.module';
import { SUMMER_PROMO_2026_MODAL_QUERY_PARAM } from 'reactApp/modules/promoNew/items/summerCampaign2026ModalCreator';
import { loadQuotaTariffsLandingRequest, sendShowLandingAnalyticsAction } from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { useRenewFromQuery } from 'reactApp/sections/MobileQuotaTariffsLanding/hooks/useRenewFromQuery';
import { useShowSubscriptionModalFromQuery } from 'reactApp/sections/MobileSubscriptionsPage/hooks/useShowSubscriptionModalFromQuery';
import {
    useAddCardModal,
    useRemoveCardModal,
    useSubscriptionsModal,
} from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.hooks';
import { Container } from 'reactApp/sections/QuotaLanding/Container/Container';
import { useAutoUploadOnboardingFromQuery } from 'reactApp/sections/QuotaLanding/hooks/useAutoUploadOnboardingFromQuery';
import { useAutoUploadPromoBlock } from 'reactApp/sections/QuotaLanding/hooks/useAutoUploadPromoBlock';
import { useProductFromQuery } from 'reactApp/sections/QuotaLanding/hooks/useProductFromQuery';
import { useUpgradeFromQuery } from 'reactApp/sections/QuotaLanding/hooks/useUpgradeFromQuery';
import { MultiTariffsListMobile } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffsListMobile';
import { PromoMarch2026 } from 'reactApp/sections/QuotaLanding/PromoMarch2026/PromoMarch2026';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { SummerPromo2026 } from 'reactApp/sections/QuotaLanding/SummerPromo2026/SummerPromo2026';
import { SummerPromo2026BackgroundImages } from 'reactApp/sections/QuotaLanding/SummerPromo2026BackgroundImages/SummerPromo2026BackgroundImages';
import { TariffsList } from 'reactApp/sections/QuotaLanding/TariffsList/TairiffsList';
import { Button } from 'reactApp/ui/Button/Button';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import Content from 'reactApp/ui/Content/Content';
import { renderSubscriptionsModal } from 'reactApp/ui/Mobile/SubscriptionsModal/SubscriptionsModal.helpers';
import { openSummerCampaign2026Modal } from 'reactApp/ui/SummerCampaign2026Modal/SummerCampaign2026Modal.helpers';
import { sendIosFavorableTariffsAnalytics, sendProfitableTariffsFirstAnalytics } from 'reactApp/utils/analytics';
import opener from 'reactApp/utils/opener';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';

import { useVKIDFromQuery } from '../QuotaLanding/hooks/useVKIDFromQuery';
import { PromoBlackFriday2025 } from '../QuotaLanding/PromoBlackFriday2025/PromoBlackFriday2025';
import { FeaturesCarousel } from './FeaturesCarousel/FeaturesCarousel';
import { Header } from './Header/Header';
import styles from './MobileQuotaTariffsLanding.css';
import { sendWebviewAnalytics } from './MobileQuotaTariffsLanding.helpers';

const HELP_LINK = 'https://help.mail.ru/cloud_web/size/off/';

const queryParams = getQueryParams();

export const MobileQuotaTariffsLanding = memo((): ReactElement => {
    const dispatch = useDispatch();

    const { isLoading, hasError, isLoaded } = useSelector(QuotaLandingSelectors.getState);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const isUserWithBilling = useSelector(UserSelectors.isUserWithBilling);
    const tariffsRef = useRef<HTMLDivElement>(null);

    const isOverQuotaCloud = useSelector(UserSelectors.isOverQuotaUser);
    const isOverquotaFromProfileApi = useSelector(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const isOverQuotaMailAndCloud = useSelector(UserQuotaSelectors.isOverquota);
    const isOverQuotaUser = isOverQuotaCloud || isOverquotaFromProfileApi || isOverQuotaMailAndCloud;
    const isMultiTariff = multiTariffMobile && multiTariffMobile !== 'a';
    const blackFriday2025Enabled = blackFriday2025 && !isOverQuotaUser;
    const marchPromo2026Enabled = useMarchPromo2026MailSpace();
    const summerPromo2026Enabled = useSummerPromo2026();

    const isMobilePayAvailable = checkMobileWebviewPayAvailable();

    const autouploadPromoBlock = useAutoUploadPromoBlock({
        isMobilePayAvailable,
        isIOSPromo: IS_IOS_MOBILE,
    });

    useAutoUploadOnboardingFromQuery();
    useVKIDFromQuery();
    useProductFromQuery({
        forceMobile: true,
        mobileInfo: {
            isMobilePayAvailable,
            isIOSPromo: IS_IOS_MOBILE,
        },
    });
    useRenewFromQuery({
        isMobilePayAvailable,
        isIOSPromo: IS_IOS_MOBILE,
    });
    useRemoveCardModal();
    useAddCardModal();
    useSubscriptionsModal();
    useUpgradeFromQuery();

    useDarkTheme(isDarkTariffsPages);

    const onSubscriptionsClick = useCallback(() => {
        sendQuotaGa({ action: 'subscriptions', label: 'click' });
        renderSubscriptionsModal({ isRebrandingQuotaLanding, isRebranding });
    }, []);

    const onCancelInfo = useCallback(() => {
        sendQuotaGa({ action: 'cancel-info', label: 'click' });
    }, []);

    useEffect(() => {
        dispatch(loadQuotaTariffsLandingRequest());
        emitAnalyticEvent('MOBILE_LANDING_SHOWN');
        sendIosFavorableTariffsAnalytics(['landing_view']);
        sendProfitableTariffsFirstAnalytics(['landing_view']);
        sendTmrGoal({ id: TMR3473039, goal: 'landing-view' });
    }, []);

    const retry = useCallback(() => {
        dispatch(loadQuotaTariffsLandingRequest());
    }, [dispatch]);

    useEffect(() => {
        if (isLoaded) {
            sendWebviewAnalytics('open', isMobilePayAvailable);
            dispatch(sendShowLandingAnalyticsAction());
        }
    }, [isLoaded]);

    useEffect(() => {
        const { action = null } = getQueryParams();
        if (action === 'openSubsModal') {
            renderSubscriptionsModal({ isRebrandingQuotaLanding, isRebranding });
        }
    }, []);

    // CLOUD-4802: получаем протухшие подписки
    useEffect(() => {
        dispatch(getPastSubscriptionsIfNotLoaded());
    }, [dispatch]);

    useEffect(() => {
        if (isRebrandingQuotaLanding) {
            setRebrandingQuota();
        }

        return () => clearRebrandingQuota();
    }, [isRebrandingQuotaLanding]);

    useEffect(() => {
        if (isLoaded && queryParams?.[SUMMER_PROMO_2026_MODAL_QUERY_PARAM]) {
            openSummerCampaign2026Modal();
        }
    }, [isLoaded, queryParams]);

    // если пользователю недоступен билинг
    // если аноним
    // если не тач
    // редирект к хомяк
    if (!isUserWithBilling || isAnonymous || !IS_MOBILE_BROWSER) {
        xray.send('mobile_quota_tariffs_to_home');
        opener('/', true);
    }

    useShowSubscriptionModalFromQuery();

    return (
        <Content
            buttonText="Попробовать ещё раз"
            retry={retry}
            isLoading={isLoading}
            hasError={hasError}
            isPage
            pageName="quota_landing_tariffs"
        >
            <div
                className={classNames(styles.root, {
                    [styles.root_dark]: isDarkTariffsPages,
                    [styles.root_blackFriday2025]: blackFriday2025Enabled,
                    [styles.root_marchPromo2026]: marchPromo2026Enabled,
                })}
            >
                <>
                    <Spacing size={blackFriday2025Enabled ? 32 : 24} />
                    <Header
                        blackFriday2025Enabled={blackFriday2025Enabled}
                        marchPromo2026Enabled={marchPromo2026Enabled}
                        summerPromo2026Enabled={summerPromo2026Enabled}
                    />
                    <Spacing size={autouploadPromoBlock ? 20 : blackFriday2025Enabled ? 12 : 32} />
                </>

                {summerPromo2026Enabled && (
                    <>
                        <SummerPromo2026BackgroundImages isMobile={true} isWebview={true} />
                        <SummerPromo2026 isMobile={true} />
                        <Spacing size={20} />
                    </>
                )}

                {marchPromo2026Enabled && (
                    <>
                        <img
                            src={`${cdnPackagesUrl}/static/cloud/promo/march2026/webview/bg.webp`}
                            className={styles.marchPromo2026WebviewImg}
                        />
                        <PromoMarch2026
                            isMobile={true}
                            isIOSPromo={IS_IOS_MOBILE}
                            isAndroidPromo={IS_ANDROID_MOBILE}
                            isMobilePayAvailable={isMobilePayAvailable}
                        />
                        <Spacing size={24} />
                    </>
                )}

                {blackFriday2025Enabled && (
                    <>
                        <div className={styles.promoBlackFriday2025}>
                            <PromoBlackFriday2025
                                isIOSPromo={IS_IOS_MOBILE}
                                isAndroidPromo={!IS_IOS_MOBILE}
                                isMobilePayAvailable={isMobilePayAvailable}
                            />
                        </div>
                        <Spacing size={20} />
                    </>
                )}

                {/* если акция, то не будет рендерится*/}
                {autouploadPromoBlock && (
                    <>
                        {autouploadPromoBlock}
                        <Spacing size={4} />
                    </>
                )}
                {isMultiTariff ? (
                    <MultiTariffsListMobile
                        ref={tariffsRef}
                        isAnonymous={isAnonymous}
                        isTariffsPage={true}
                        isIOSPromo={IS_IOS_MOBILE}
                        isMobilePayAvailable={isMobilePayAvailable}
                        isMobilePayFromGetParam={false}
                        blackFriday2025Enabled={blackFriday2025Enabled}
                        marchPromo2026Enabled={marchPromo2026Enabled}
                    />
                ) : (
                    <TariffsList
                        ref={tariffsRef}
                        isIOSPromo={IS_IOS_MOBILE}
                        isMobilePayAvailable={isMobilePayAvailable}
                        isMobilePayFromGetParam={false}
                        isAnonymous={isAnonymous}
                        minTariff={queryParams?.min_tariff}
                        isTariffsPage
                        isPhone
                    />
                )}
                {/* tempexp_17906-next-line */}
                {mailSpaceBenifits !== 'a' ? <FeaturesCarousel /> : <Spacing size={38} />}
                <Container isNarrow>
                    <Button className={classNames(styles.button)} theme="vk" onClick={onSubscriptionsClick} qaId="subscriptions">
                        Мои подписки
                    </Button>
                    <Spacing size={10} />
                    <Button
                        className={classNames(styles.button)}
                        theme="vk"
                        href={actionTermsLink || NEW_ACTION_TERMS_LINK}
                        target="_blank"
                        qaId="action"
                    >
                        Полные условия акции
                    </Button>
                    <Spacing size={10} />
                    <ButtonLink
                        className={styles.link}
                        href={HELP_LINK}
                        target="_blank"
                        rel="noopener"
                        hoverUnderline={false}
                        onClick={onCancelInfo}
                    >
                        Как отменить подписку?
                    </ButtonLink>
                    {Boolean(promoQuotaAllTariffsLink) && (
                        <ButtonLink
                            className={styles.link}
                            href={promoQuotaAllTariffsLink}
                            target="_blank"
                            rel="noopener"
                            hoverUnderline={false}
                        >
                            Все тарифы
                        </ButtonLink>
                    )}
                </Container>
                <Spacing size={32} />
            </div>
        </Content>
    );
});

MobileQuotaTariffsLanding.displayName = 'MobileQuotaTariffsLanding';
